import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import useTier from '../hooks/useTier';

const TIERS = ['free', 'premium', 'pro'];
const TIER_COLORS = { free: 'bg-gray-500', premium: 'bg-[var(--aif-color-primary)]', pro: 'bg-[var(--aif-color-accent)]' };
// A9 Sprint 3 drift-fix: TIER_PRICES/TIER_LIMITS now derived from useTier (canonical /billing/tiers)
// Old hardcoded values were WRONG: premium '49' (canonical 29), pro '149' (canonical 79)
// Old limits WRONG: free 10 (canonical 20), premium 500 (canonical 100), pro 2000 (canonical 1000)

const FEATURES = [
  { key: 'chat', free: true, premium: true, pro: true },
  { key: 'weather', free: true, premium: true, pro: true },
  { key: 'subsidies', free: true, premium: true, pro: true },
  { key: 'fields', free: false, premium: true, pro: true },
  { key: 'market', free: false, premium: true, pro: true },
  { key: 'herd', free: false, premium: true, pro: true },
  { key: 'energy', free: false, premium: false, pro: true },
  { key: 'compliance', free: false, premium: true, pro: true },
  { key: 'support', free: false, premium: false, pro: true },
];

export default function SubscriptionPage({ onClose, user, token }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  // A9 Sprint 3: canonical tier data from backend
  const { pricing, features: tiersData } = useTier();

  const TIER_PRICES = {
    free: '0',
    premium: pricing?.premium?.monthly?.toString() || '29',
    pro: pricing?.pro?.monthly?.toString() || '79',
  };
  const TIER_LIMITS = {
    free: tiersData?.free?.features?.chat_daily_limit || 20,
    premium: tiersData?.premium?.features?.chat_daily_limit || 100,
    pro: tiersData?.pro?.features?.chat_daily_limit || 1000,
  };

  const tier = user?.subscription_tier || 'free';
  const messagesUsed = user?.messages_today || 0;
  const dailyLimit = user?.daily_limit || TIER_LIMITS[tier] || 10;

  const handleUpgrade = async (priceTier) => {
    setLoading(true);
    try {
      const res = await apiClient.post('/api/v1/aif/billing/create-checkout-session', {
        price_id: priceTier === 'premium' ? 'price_premium' : 'price_pro',
        success_url: window.location.origin + '/billing/success',
        cancel_url: window.location.origin,
      }, { queueOnOffline: true });
      window.location.href = (res.data || res).checkout_url;
    } catch { /* ApiError */ }
    setLoading(false);
  };

  const handleManage = async () => {
    setLoading(true);
    try {
      const res = await apiClient.post('/api/v1/aif/billing/create-portal-session', {}, { queueOnOffline: true });
      window.location.href = (res.data || res).portal_url;
    } catch { /* ApiError */ }
    setLoading(false);
  };

  const usagePct = Math.min((messagesUsed / dailyLimit) * 100, 100);

  return (
    <div className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col animate-slideInRight">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
        </button>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('menu.subscription')}</span>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-lg mx-auto px-6 py-6 space-y-6">
          {/* Current plan badge */}
          <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 text-xs font-semibold text-white rounded-full ${TIER_COLORS[tier]}`}>
                {t(`upgrade.${tier}`)}
              </span>
              <span className="text-sm text-[var(--aif-text-muted)]">{t('upgrade.currentPlan')}</span>
            </div>

            {/* Usage bar */}
            <div>
              <div className="flex justify-between text-xs text-[var(--aif-text-muted)] mb-1.5">
                <span>{t('subscription.usage')}</span>
                <span>{messagesUsed} / {dailyLimit} {t('subscription.perDay')}</span>
              </div>
              <div className="h-2 bg-[var(--aif-bg-elevated)] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${usagePct >= 90 ? 'bg-[var(--aif-color-danger)]' : 'bg-[var(--aif-color-primary)]'}`}
                  style={{ width: `${usagePct}%` }}
                />
              </div>
            </div>
          </div>

          {/* Feature comparison */}
          <div>
            <h3 className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wider mb-3">{t('subscription.comparison')}</h3>
            <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-2xl overflow-hidden">
              {/* Header row */}
              <div className="grid grid-cols-4 gap-0 px-4 py-3 border-b border-[var(--aif-border-default)]">
                <div />
                {TIERS.map(t2 => (
                  <div key={t2} className="text-center">
                    <div className="text-xs font-semibold text-[var(--aif-text-primary)]">{t(`upgrade.${t2}`)}</div>
                    <div className="text-[10px] text-[var(--aif-text-muted)]">
                      {TIER_PRICES[t2] === '0' ? t('upgrade.free') : `€${TIER_PRICES[t2]}/m`}
                    </div>
                  </div>
                ))}
              </div>

              {/* Message limits row */}
              <div className="grid grid-cols-4 gap-0 px-4 py-2.5 border-b border-[var(--aif-border-default)]/50">
                <div className="text-xs text-[var(--aif-text-secondary)]">{t('upgrade.features.chat')}</div>
                {TIERS.map(t2 => (
                  <div key={t2} className="text-center text-xs text-[var(--aif-text-muted)]">
                    {TIER_LIMITS[t2]}/d
                  </div>
                ))}
              </div>

              {/* Feature rows */}
              {FEATURES.filter(f => f.key !== 'chat').map(feat => (
                <div key={feat.key} className="grid grid-cols-4 gap-0 px-4 py-2.5 border-b border-[var(--aif-border-default)]/50 last:border-b-0">
                  <div className="text-xs text-[var(--aif-text-secondary)]">{t(`upgrade.features.${feat.key}`)}</div>
                  {TIERS.map(t2 => (
                    <div key={t2} className="text-center">
                      {feat[t2] ? (
                        <svg className="w-4 h-4 mx-auto text-[var(--aif-color-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      ) : (
                        <span className="text-xs text-[var(--aif-text-muted)]">—</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Action buttons */}
          {tier === 'free' ? (
            <div className="space-y-3">
              <button
                onClick={() => handleUpgrade('premium')}
                disabled={loading}
                className="w-full py-3 text-sm font-medium rounded-2xl transition bg-[var(--aif-color-primary)] text-white hover:opacity-90 disabled:opacity-50"
              >
                {t('upgrade.upgradeNow')} — Premium €{TIER_PRICES.premium}/m
              </button>
              <button
                onClick={() => handleUpgrade('pro')}
                disabled={loading}
                className="w-full py-3 text-sm font-medium rounded-2xl transition bg-[var(--aif-color-accent)] text-black hover:opacity-90 disabled:opacity-50"
              >
                {t('upgrade.upgradeNow')} — Pro €{TIER_PRICES.pro}/m
              </button>
            </div>
          ) : (
            <button
              onClick={handleManage}
              disabled={loading}
              className="w-full py-3 text-sm font-medium rounded-2xl transition border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] text-[var(--aif-text-primary)] hover:bg-[var(--aif-bg-elevated)] disabled:opacity-50"
            >
              {t('subscription.manage')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
